import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import Select from "react-select";
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventIcon from "@mui/icons-material/Event";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
import EmptyState from "../common/EmptyState";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const CalendarView = () => {
  const [events, setEvents] = useState({ tasks: [] });
  const [selectedDate, setSelectedDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [eventData, setEventData] = useState({
    name: "",
    time: "",
    endTime: "",
    date: new Date(),
    recurrence: "none",
    employees: [],
  });
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [error, setError] = useState(null);
  const [selectedDateEvents, setSelectedDateEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    name: "",
    time: "",
  });

  const fetchEvents = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(
        `${BASE_URL}/api/holidays/logineduser/events`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Error fetching events");

      const data = await response.json();
      setEvents({ tasks: data.tasks });
    } catch (err) {
      console.error("Error fetching events:", err);
      setError("Error fetching events");
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${BASE_URL}/api/employees`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("Error fetching employees");

      const data = await response.json();
      // Check if the data is an array and contains the expected fields
      const formattedEmployees = Array.isArray(data)
        ? data.map((employee) => ({
            value: employee.employeeid,
            label: `${employee.name} (${employee.employeeid})`,
          }))
        : [];

      setEmployeeOptions([
        { value: "all", label: "Select All" },
        ...formattedEmployees,
      ]);
    } catch (err) {
      console.error("Error fetching employees:", err);
      setError("Error fetching employees");
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchEmployees();
  }, []);

  const openEventList = (date) => {
    setSelectedDate(date);
    const dayEvents = events.tasks.filter(
      (task) => new Date(task.date).toDateString() === date.toDateString()
    );
    setSelectedDateEvents(dayEvents);
  };

  const handleAddEventClick = () => {
    console.log("handleventclick hitted");
    // Reset the event data to initial state
    setEventData({
      name: "",
      time: "",
      endTime: "",
      date: new Date(),
      recurrence: "none",
      employees: [],
      _id: null, // Add this if you're using it for edit functionality
    });

    // Retrieve the logged-in user's employee ID from sessionStorage
    const token = sessionStorage.getItem("token");
    if (!token) {
      setError("No token found. Please log in.");
      return;
    }

    const decodedToken = JSON.parse(atob(token.split(".")[1]));
    const loggedInUserId = decodedToken.employee.employeeid;
    console.log("loggedInUserId", loggedInUserId);

    // Find the employee object corresponding to the logged-in user
    const loggedInEmployee = employeeOptions.find(
      (employee) => employee.value === loggedInUserId
    );

    // If the logged-in user exists in the employee list, pre-select them
    if (loggedInEmployee) {
      setEventData((prev) => ({
        ...prev,
        employees: [loggedInEmployee], // Automatically select the logged-in employee
      }));
    }

    setIsModalOpen(true);
  };

  const generateRecurringDates = (startDate, recurrence) => {
    const dates = [];
    const maxOccurrences = 30;
    // const maxOccurrences = {
    //   daily: 1825,
    //   weekly: 260,
    //   monthly: 60,
    // };
    let currentDate = new Date(startDate);

    switch (recurrence) {
      case "daily":
        for (let i = 0; i < maxOccurrences; i++) {
          dates.push(new Date(currentDate));
          currentDate.setDate(currentDate.getDate() + 1);
        }
        break;
      case "weekly":
        for (let i = 0; i < maxOccurrences; i++) {
          dates.push(new Date(currentDate));
          currentDate.setDate(currentDate.getDate() + 7);
        }
        break;
      case "monthly":
        for (let i = 0; i < maxOccurrences; i++) {
          dates.push(new Date(currentDate));
          currentDate.setMonth(currentDate.getMonth() + 1);
        }
        break;
      default:
        dates.push(new Date(currentDate));
        break;
    }
    return dates;
  };

  const handleEmployeeSelect = (selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      setEventData({
        ...eventData,
        employees: [],
      });
      return;
    }

    // If 'Select All' is chosen, include all employees, otherwise only the selected ones.
    if (selectedOptions.some((option) => option.value === "all")) {
      setEventData({
        ...eventData,
        employees: employeeOptions.filter((option) => option.value !== "all"),
      });
    } else {
      setEventData({
        ...eventData,
        employees: selectedOptions,
      });
    }
  };

  const handleSaveEvent = async () => {
    setIsLoading(true); // Start loading spinner
    setError(null); // Clear any previous errors
    const nameError = !eventData.name?.trim() ? "Event Name is required" : "";
    const timeError = !eventData.time?.trim() ? "Event Time is required" : "";

    if (nameError || timeError) {
      setValidationErrors({
        name: nameError,
        time: timeError,
      });
      setIsLoading(false);
      return;
    }

    setValidationErrors({ name: "", time: "" });

    try {
      const token = sessionStorage.getItem("token");

      const validEmployees = eventData.employees.filter(
        (emp) => emp && emp.value
      );
      const eventDates = generateRecurringDates(
        eventData.date,
        eventData.recurrence
      );
      const employeeIds = validEmployees.map((employee) => employee.value);

      const notificationMessage = `Updated Event: ${eventData.name} scheduled at ${eventData.time}, Recurrence: ${eventData.recurrence}`;

      for (const date of eventDates) {
        const data = {
          _id: eventData._id,
          name: eventData.name,
          time: eventData.time,
          endTime: eventData.endTime,
          date: date.toISOString(),
          externalEmployeeIds: employeeIds,
        };
        console.log("data", data);

        // Determine the correct method and URL based on whether an event ID exists
        const isEditMode = !!eventData._id;
        const url = isEditMode
          ? `${BASE_URL}/api/holidays/edittask/${eventData._id}`
          : `${BASE_URL}/api/holidays/tasks`;
        console.log("eventData._id", eventData._id);
        console.log("eventData", eventData);
        const method = isEditMode ? "PUT" : "POST";

        // Make the appropriate API request
        const response = await fetch(url, {
          method: method,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });

        const result = await response.json();

        if (!response.ok) {
          const errorMessage = result.error || "Error saving event";
          setError(`${errorMessage}`);
          throw new Error(errorMessage);
        }
      }

      // Send email notification for updated events
      await fetch(`${BASE_URL}/api/notification/send`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: notificationMessage,
          employeeIds,
        }),
      });

      setShowSuccessPopup(true);
      setTimeout(() => setShowSuccessPopup(false), 1500);

      setIsModalOpen(false); // Close modal after saving
      fetchEvents(); // Refresh events after saving
    } catch (err) {
      console.error("Error saving event:", err);
      setError(`${err.message || "An unexpected error occurred"}`);
    } finally {
      setIsLoading(false); // Stop loading spinner
    }
  };

  const handleEditEvent = (event) => {
    // Open the modal and set event data to be edited
    setEventData({
      _id: event._id,
      name: event.name,
      time: event.time,
      endTime: event.endTime,
      date: new Date(event.date),
      recurrence: event.recurrence,
      employees: event.employees || [], // Ensure employees are set if they exist
    });

    setIsModalOpen(true); // Open the modal
  };

  const handleDeleteTask = async (taskId) => {
    console.log("hitted");
    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(
        `${BASE_URL}/api/holidays/deletetask/${taskId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      console.log("response", response);
      console.log("taskId", taskId);
      const data = await response.json();
      console.log("data", data);
      if (response.ok) {
        // Successfully deleted, remove the task from the state
        setSelectedDateEvents((prevEvents) =>
          prevEvents.filter((event) => event._id !== taskId)
        );
        alert("Task deleted successfully!");
      } else {
        alert(data.error || "Error deleting task");
      }
    } catch (err) {
      console.error("Error deleting task:", err);
      alert("Error deleting task");
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      fontSize: "0.875rem",
      "& fieldset": { borderColor: "#eef0f6" },
      "&:hover fieldset": { borderColor: "#8b8fa3" },
      "&.Mui-focused fieldset": { borderColor: "#667eea", borderWidth: 1.5 },
    },
  };

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="Event Calendar"
        subtitle="Manage events, tasks, and schedules for your team"
        icon={<CalendarMonthIcon />}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddEventClick}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "0.875rem",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              px: 2.5,
              boxShadow: "none",
              "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)", boxShadow: "none" },
            }}
          >
            Add Event
          </Button>
        }
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          alignItems: "flex-start",
        }}
      >
        {/* LEFT - Calendar */}
        <Paper
          elevation={0}
          sx={{
            flex: 1.2,
            border: "1px solid #eef0f6",
            borderRadius: "16px",
            p: 3,
            "& .react-calendar": {
              width: "100%",
              border: "none",
              fontFamily: "inherit",
              lineHeight: 1.8,
            },
            "& .react-calendar__tile": {
              borderRadius: "8px",
              fontSize: "0.875rem",
              padding: "8px",
            },
            "& .react-calendar__tile--active": {
              bgcolor: "#667eea !important",
              color: "#fff !important",
              borderRadius: "8px",
              fontWeight: 700,
            },
            "& .react-calendar__tile--now": {
              bgcolor: "#eef0ff !important",
              color: "#1a1a2e !important",
              border: "1px solid #c7d2fe",
              borderRadius: "8px",
            },
            "& .react-calendar__tile--now.react-calendar__tile--active": {
              bgcolor: "#4f46e5 !important",
              color: "#ffffff !important",
              border: "2px solid #312e81",
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.25)",
              fontWeight: 700,
            },
            "& .react-calendar__navigation button": {
              fontSize: "1rem",
              fontWeight: 600,
              color: "#1a1a2e",
              borderRadius: "8px",
              "&:hover": { bgcolor: "#f5f7fb" },
            },
            "& .react-calendar__month-view__weekdays": {
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#8b8fa3",
              textTransform: "uppercase",
            },
            "& .react-calendar__month-view__weekdays abbr": {
              textDecoration: "none",
            },
          }}
        >
          <Calendar
            onClickDay={(date) => openEventList(date)}
            value={selectedDate}
            tileContent={({ date }) => {
              const dayEvents = events.tasks.filter(
                (task) =>
                  new Date(task.date).toDateString() === date.toDateString()
              );
              return dayEvents.length > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 0.25,
                  }}
                >
                  <Chip
                    label={`${dayEvents.length}`}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      bgcolor: "#667eea",
                      color: "#fff",
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                </Box>
              ) : null;
            }}
          />
        </Paper>

        {/* RIGHT - Selected Date Events */}
        <Paper
          elevation={0}
          sx={{
            flex: 0.8,
            border: "1px solid #eef0f6",
            borderRadius: "16px",
            p: 3,
            minHeight: 400,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "1rem",
              color: "#1a1a2e",
              pb: 2,
              mb: 2,
              borderBottom: "2px solid #f0f0f5",
            }}
          >
            {selectedDate
              ? `Events on ${selectedDate.toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}`
              : "Select a Date"}
          </Typography>

          {!selectedDate ? (
            <EmptyState
              icon={<EventIcon sx={{ fontSize: 28 }} />}
              title="No date selected"
              subtitle="Click on a date in the calendar to view its events."
              sx={{ py: 6 }}
            />
          ) : selectedDateEvents.length === 0 ? (
            <EmptyState
              icon={<EventIcon sx={{ fontSize: 28 }} />}
              title="No events"
              subtitle="There are no events scheduled for this date."
              sx={{ py: 6 }}
            />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {selectedDateEvents.map((event, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    border: "1px solid #eef0f6",
                    borderRadius: "8px",
                    bgcolor: "#FAFBFC",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    transition: "border-color 0.2s",
                    "&:hover": { borderColor: "#8b8fa3" },
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: "#1a1a2e",
                        mb: 0.5,
                      }}
                    >
                      {event.name}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <AccessTimeIcon
                        sx={{ fontSize: 14, color: "#8b8fa3" }}
                      />
                      <Typography
                        sx={{ fontSize: "0.8rem", color: "#8b8fa3" }}
                      >
                        {event.time} - {event.endTime}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditEvent(event)}
                      title="Edit Task"
                      sx={{
                        color: "#8b8fa3",
                        "&:hover": { color: "#667eea", bgcolor: "#eef0ff" },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteTask(event._id)}
                      title="Delete Task"
                      sx={{
                        color: "#8b8fa3",
                        "&:hover": { color: "#DC2626", bgcolor: "#FEF2F2" },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      </Box>

      {/* Add/Edit Event Dialog */}
      <Dialog
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEventData({
            name: "",
            time: "",
            endTime: "",
            date: new Date(),
            recurrence: "none",
            employees: [],
            _id: null,
          });
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px", p: 1 },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "1.15rem",
            color: "#1a1a2e",
            pb: 1,
          }}
        >
          {eventData._id ? "Edit Event" : "Add New Event"}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: "8px", fontSize: "0.875rem" }}
            >
              {error}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#5a5f7a", mb: 0.75 }}
              >
                Event Name *
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter event name"
                value={eventData.name}
                onChange={(e) => {
                  setEventData({ ...eventData, name: e.target.value });
                  if (validationErrors.name) {
                    setValidationErrors((prev) => ({ ...prev, name: "" }));
                  }
                }}
                error={Boolean(validationErrors.name)}
                helperText={validationErrors.name}
                sx={inputSx}
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#5a5f7a", mb: 0.75 }}
                >
                  Start Time
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="time"
                  value={eventData.time || ""}
                  onChange={(e) => {
                    setEventData({ ...eventData, time: e.target.value });
                    if (validationErrors.time) {
                      setValidationErrors((prev) => ({ ...prev, time: "" }));
                    }
                  }}
                  error={Boolean(validationErrors.time)}
                  helperText={validationErrors.time}
                  sx={inputSx}
                />
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#5a5f7a", mb: 0.75 }}
                >
                  End Time
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="time"
                  value={eventData.endTime || ""}
                  onChange={(e) =>
                    setEventData({ ...eventData, endTime: e.target.value })
                  }
                  sx={inputSx}
                />
              </Box>
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#5a5f7a", mb: 0.75 }}
              >
                Date
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={eventData.date.toISOString().split("T")[0]}
                onChange={(e) =>
                  setEventData({
                    ...eventData,
                    date: new Date(e.target.value),
                  })
                }
                sx={inputSx}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#5a5f7a", mb: 0.75 }}
              >
                Assign Employees
              </Typography>
              <Select
                isMulti
                options={employeeOptions}
                value={eventData.employees}
                onChange={handleEmployeeSelect}
                placeholder="Select employees..."
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                menuPlacement="auto"
                styles={{
                  control: (base, state) => ({
                    ...base,
                    border: state.isFocused
                      ? "1.5px solid #667eea"
                      : "1px solid #eef0f6",
                    boxShadow: "none",
                    minHeight: "40px",
                    borderRadius: 8,
                    fontSize: "0.875rem",
                    "&:hover": { borderColor: "#8b8fa3" },
                  }),
                  indicatorSeparator: () => ({ display: "none" }),
                  menu: (base) => ({
                    ...base,
                    border: "1px solid #eef0f6",
                    borderRadius: 8,
                    zIndex: 1301,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  }),
                  menuList: (base) => ({
                    ...base,
                    maxHeight: 200,
                    overflowY: "auto",
                  }),
                  option: (base, { isFocused, isSelected }) => ({
                    ...base,
                    backgroundColor: isSelected
                      ? "#EBF0FF"
                      : isFocused
                      ? "#f5f7fb"
                      : "transparent",
                    color: "#1a1a2e",
                    fontSize: "0.875rem",
                    cursor: "pointer",
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#EBF0FF",
                    borderRadius: 4,
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#667eea",
                    fontSize: "0.8rem",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#667eea",
                    "&:hover": { backgroundColor: "#667eea", color: "#fff" },
                  }),
                }}
                menuPosition="absolute"
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#5a5f7a", mb: 0.75 }}
              >
                Recurrence
              </Typography>
              <TextField
                fullWidth
                size="small"
                select
                value={eventData.recurrence}
                onChange={(e) =>
                  setEventData({ ...eventData, recurrence: e.target.value })
                }
                sx={inputSx}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </TextField>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setIsModalOpen(false);
              setEventData({
                name: "",
                time: "",
                endTime: "",
                date: new Date(),
                recurrence: "none",
                employees: [],
                _id: null,
              });
            }}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "0.875rem",
              borderColor: "#CBD5E1",
              color: "#5a5f7a",
              px: 3,
              "&:hover": { borderColor: "#8b8fa3", bgcolor: "#f5f7fb" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEvent}
            disabled={isLoading}
            startIcon={
              isLoading ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : null
            }
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "0.875rem",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              px: 3,
              boxShadow: "none",
              "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)", boxShadow: "none" },
              "&:disabled": { bgcolor: "#8b8fa3", color: "#fff" },
            }}
          >
            {isLoading ? "Saving..." : "Save Event"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessPopup}
        autoHideDuration={1500}
        onClose={() => setShowSuccessPopup(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="success"
          sx={{ borderRadius: "8px", fontWeight: 500 }}
          onClose={() => setShowSuccessPopup(false)}
        >
          Event saved successfully!
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default CalendarView;
